import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import Constants from "../../../../constants/Index";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import { ButtonV2, OptionV2, RemoveButtonV2 } from "../../../../components/FormControlsV2";
import { OnboardingFooterV2 } from "../../../../components/OnboardingV2/OnboardingFooterV2";
import { MicroAreaFormV2, MicroAreaFormValueV2 } from "../../../../components/FormsV2";
import {
  fromMicroAreaFormValue,
  toAreaOptions,
  toFarmOptions,
  toMicroAreaFormValue,
} from "../adapters/propertyForms";
import {
  persistPayload,
} from "../adapters/payload";
import { hasErrors, validateV2, ValidationErrorsV2 } from "../adapters/validation";
import { generateuuid } from "../../../../utils/uuid/generate";
import { DrawOnMapSlotV2 } from "../../../../components/OnboardingV2/DrawOnMapSlotV2";
import { calculateHectares } from "../../../../utils/geolib/calculateHectares";

const microAreaSchema = yup.object().shape({
  farm: yup.object().nullable().required("Fazenda é necessária."),
  area: yup.object().nullable().required("Retiro é necessário."),
  initials: yup.string().required("SIGLA é necessário."),
  acreage: yup.string().required("Campo obrigatório não informado."),
  map_coords: yup.array().min(1, Constants.MESSAGE_MAPPING_MICROAREA_NOT_FOUND),
});

function createMicroAreaBase(farm: OptionV2 | null, area: OptionV2 | null, index: number): MicroAreaFormValueV2 {
  return {
    uuid: generateuuid(),
    farm,
    area,
    initials: String(index + 1).padStart(3, "0"),
    acreage: "0",
    reason: null,
    map_coords: [],
  };
}

type CreateMicroAreasByFarmV2Props = {
  farmId: string;
};

export function CreateMicroAreasByFarmV2({ farmId }: CreateMicroAreasByFarmV2Props) {
  const { payload, setPayload, setBlockNextStep, apiFieldErrors, setApiFieldErrors, clearStepError } = useOnboarding();
  const farmOptions = useMemo(() => toFarmOptions(payload), [payload]);
  const activeFarm = farmOptions.find((farm) => String(farm.value) === String(farmId)) || null;
  const areaOptions = useMemo(() => toAreaOptions(payload, activeFarm ? String(activeFarm.value) : ""), [payload, activeFarm?.value]);
  const [microAreas, setMicroAreas] = useState<MicroAreaFormValueV2[]>([]);
  const [errors, setErrors] = useState<Record<string, ValidationErrorsV2>>({});

  async function validateMicroAreas(microAreasToValidate = microAreas) {
    const validationEntries = await Promise.all(
      microAreasToValidate.map(async (microArea, index) => {
        const validation = await validateV2(microAreaSchema, microArea);
        const sameInitials = microAreasToValidate.filter(
          (item) => item.area?.value === microArea.area?.value && item.initials && item.initials === microArea.initials
        ).length;
        if (sameInitials > 1) validation.initials = Constants.MESSAGE_UNIQUE_INITIALS_MICROAREA;
        return [String(index), validation] as const;
      })
    );
    const validation = Object.fromEntries(validationEntries);
    setErrors(validation);
    const invalid = Object.values(validation).some(hasErrors);
    const hasApiErrors = hasMicroAreaApiErrors(microAreasToValidate);
    setBlockNextStep(invalid || hasApiErrors);
    if (!invalid && !hasApiErrors) clearMicroAreaStepError();
    return !invalid && !hasApiErrors;
  }

  function areaPrefix(area: OptionV2 | null) {
    const areaInfo = area?.info;
    if (!activeFarm?.info?.initials || !areaInfo?.initials) return "";
    return `${activeFarm.info.initials}-${areaInfo.initials}-`;
  }

  function updateMicroArea(index: number, value: MicroAreaFormValueV2) {
    const changedFields = Object.keys(value).filter(
      (field) => microAreas[index]?.[field as keyof MicroAreaFormValueV2] !== value[field as keyof MicroAreaFormValueV2]
    );
    const nextMicroAreas = microAreas.map((micro, microIndex) => (microIndex === index ? value : micro));
    setMicroAreas(nextMicroAreas);
    clearApiMicroAreaErrors(index, changedFields);
  }

  function mapApiMicroAreaField(field: string) {
    const fieldName = field.replace(/^micro_areas\[\d+\]\./, "");
    const fieldMap = {
      mapCoords: "map_coords",
      map_coords: "map_coords",
      microAreaReasonId: "reason",
      micro_area_reason_id: "reason",
    } as Record<string, string>;
    return fieldMap[fieldName] || fieldName;
  }

  function getActiveFarmStep() {
    const farmIndex = (payload?.farms || []).findIndex((farm: any) => String(farm.uuid || farm.id) === String(activeFarm?.value));
    return farmIndex >= 0 ? farmIndex + 2 : null;
  }

  function clearMicroAreaStepError() {
    const section = getActiveFarmStep();
    if (!activeFarm || !section) return;

    clearStepError(section, 1);
  }

  function clearApiMicroAreaErrors(index: number, changedFields: string[]) {
    if (!changedFields.length) return;

    const microAreaUuid = microAreas[index]?.uuid;
    clearApiMicroAreaErrorsByUuid(microAreaUuid, changedFields);
  }

  function clearApiMicroAreaErrorsByUuid(microAreaUuid?: string, changedFields?: string[]) {
    if (!microAreaUuid) return;

    setApiFieldErrors((current) => {
      const next = { ...current };
      Object.keys(next).forEach((field) => {
        const match = field.match(/^micro_areas\[(\d+)\]\.(.+)$/);
        if (!match) return;

        const globalMicroArea = payload?.micro_areas?.[Number(match[1])];
        if (String(globalMicroArea?.uuid) !== String(microAreaUuid)) return;
        if (!changedFields || changedFields.includes(mapApiMicroAreaField(field))) delete next[field];
      });
      return next;
    });
  }

  function getMicroAreaApiErrors(index: number) {
    return Object.entries(apiFieldErrors).reduce((fieldErrors, [field, message]) => {
      const match = field.match(/^micro_areas\[(\d+)\]\.(.+)$/);
      if (!match) return fieldErrors;

      const globalMicroArea = payload?.micro_areas?.[Number(match[1])];
      if (!globalMicroArea || !areaOptions.some((area) => String(area.value) === String(globalMicroArea.area_id))) {
        return fieldErrors;
      }

      const localIndex = microAreas.findIndex((microArea) => String(microArea.uuid) === String(globalMicroArea.uuid));
      if (localIndex !== index) return fieldErrors;

      fieldErrors[mapApiMicroAreaField(field)] = message;
      return fieldErrors;
    }, {} as ValidationErrorsV2);
  }

  function getMicroAreaErrors(index: number) {
    return {
      ...getMicroAreaApiErrors(index),
      ...(errors[String(index)] || {}),
    };
  }

  function hasMicroAreaApiErrors(microAreasToMatch = microAreas) {
    return Object.keys(apiFieldErrors).some((field) => {
      const match = field.match(/^micro_areas\[(\d+)\]\./);
      if (!match) return false;

      const globalMicroArea = payload?.micro_areas?.[Number(match[1])];
      if (!globalMicroArea || !areaOptions.some((area) => String(area.value) === String(globalMicroArea.area_id))) {
        return false;
      }

      if (!microAreasToMatch.length) return true;
      return microAreasToMatch.some((microArea) => String(microArea.uuid) === String(globalMicroArea.uuid));
    });
  }

  function updateMicroAreaMapCoords(index: number, microArea: MicroAreaFormValueV2, map_coords: any[]) {
    const calculatedAcreage = map_coords.length >= 3 ? calculateHectares(map_coords) : microArea.acreage;
    updateMicroArea(index, { ...microArea, map_coords, acreage: calculatedAcreage });
    if (map_coords.length > 0) {
      setErrors((current) => {
        const nextErrors = { ...(current[String(index)] || {}) };
        delete nextErrors.map_coords;
        return { ...current, [String(index)]: nextErrors };
      });
    }
  }

  function addMicroArea() {
    const nextMicroAreas = [...microAreas, createMicroAreaBase(activeFarm, areaOptions[0] || null, microAreas.length)];
    setMicroAreas(nextMicroAreas);
  }

  function removeMicroArea(index: number) {
    clearApiMicroAreaErrorsByUuid(microAreas[index]?.uuid);
    const nextMicroAreas =
      microAreas.length > 1 ? microAreas.filter((_, microIndex) => microIndex !== index) : microAreas;
    setMicroAreas(nextMicroAreas);
  }

  function getStaticMicroAreas(index: number, areaId?: any) {
    return microAreas
      .filter((microArea, microIndex) => microIndex !== index && String(microArea.area?.value) === String(areaId))
      .map((microArea) => microArea.map_coords)
      .filter((coords) => coords && coords.length > 0) as any;
  }

  function getAreaFitCoords(area: OptionV2 | null) {
    return area?.info?.map_coords?.coords || area?.info?.map_coords || [];
  }

  async function handleNext() {
    if (!activeFarm) return false;

    const isValid = await validateMicroAreas();
    if (!isValid) return false;

    const activeAreaIds = areaOptions.map((area) => String(area.value));
    const payloadMicroAreas = microAreas.map((microArea) =>
      fromMicroAreaFormValue(microArea, String(microArea.area?.value || ""))
    );

    setPayload((current) => {
      const next = {
        ...current,
        micro_areas: (current?.micro_areas || [])
          .filter((micro: any) => !activeAreaIds.includes(String(micro.area_id)))
          .concat(payloadMicroAreas),
      };
      persistPayload(next);
      return next as any;
    });

    return true;
  }

  useEffect(() => {
    if (!activeFarm) return;
    const existingMicroAreas = (payload?.micro_areas || []).filter((micro: any) =>
      areaOptions.some((area) => String(area.value) === String(micro.area_id))
    );
    setMicroAreas(
      existingMicroAreas.length
        ? existingMicroAreas.map((micro: any, index: number) => {
          const area = areaOptions.find((option) => String(option.value) === String(micro.area_id)) || null;
          return toMicroAreaFormValue(micro, activeFarm, area);
        })
        : [createMicroAreaBase(activeFarm, areaOptions[0] || null, 0)]
    );
  }, [activeFarm?.value, areaOptions.length, payload?.micro_areas?.length]);

  useEffect(() => {
    const invalid = Object.values(errors).some(hasErrors);
    const hasApiErrors = hasMicroAreaApiErrors();
    setBlockNextStep(invalid || hasApiErrors);
    if (!invalid && !hasApiErrors) clearMicroAreaStepError();
  }, [apiFieldErrors, areaOptions.length, microAreas.length, errors, payload?.micro_areas?.length, setBlockNextStep]);

  return (
    <>
      <main>
        <div className="onboarding-v2-form-page">
          {microAreas.map((microArea, index) => {
            const retiroFitCoords = getAreaFitCoords(microArea.area);
            const mapFitCoords = retiroFitCoords.length ? retiroFitCoords : ((microArea.map_coords || []) as any);
            return (
            <div className="onboarding-v2-list-card" key={microArea.uuid || index}>
              <MicroAreaFormV2
                value={microArea}
                farms={farmOptions}
                areas={areaOptions}
                initialsPrefix={areaPrefix(microArea.area)}
                errors={getMicroAreaErrors(index)}
                cardTitle={`Pasto ${index + 1}`}
                cardDescription={
                  microArea.initials ? `${areaPrefix(microArea.area)}${microArea.initials}` : undefined
                }
                disabledFields={["farm", "acreage"]}
                showActions={false}
                headerActions={
                  microAreas.length > 1 ? (
                    <RemoveButtonV2 onClick={() => removeMicroArea(index)} />
                  ) : undefined
                }
                mapSlot={
                  <DrawOnMapSlotV2
                    value={microArea.map_coords as any}
                    fitCoords={mapFitCoords}
                    staticsElements={getStaticMicroAreas(index, microArea.area?.value)}
                    initialsLabel={`${areaPrefix(microArea.area)}${microArea.initials || index + 1}`}
                    editable={!!microArea.area}
                    error={getMicroAreaErrors(index).map_coords}
                    onChange={(map_coords) => updateMicroAreaMapCoords(index, microArea, map_coords)}
                  />
                }
                onChange={(value) => updateMicroArea(index, value)}
              />
            </div>
            );
          })}
          <div className="onboarding-v2-page-actions">
            <ButtonV2 onClick={addMicroArea}>+ ADICIONAR PASTO</ButtonV2>
          </div>
        </div>
      </main>
      <OnboardingFooterV2 nextStepEvent={handleNext} />
    </>
  );
}
